import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { BlackbaudRaisersEdgeIntegration } from '@feathr/blackbox';
import { submitModelWithErrorHandling, toast } from '@feathr/components';
import { logUserEvents } from '@feathr/hooks';

import { isFundMissing } from './RaisersEdgeActivitiesTab/RaisersEdgeActivitiesTab.utils';
import { SOLICIT_CODE_KEY, UNSUBSCRIBE_ACTION_KEY } from './raisersEdgeConfig';
import { getChangedAttributes, getPatch } from './RaisersEdgeIntegrationPageV2.utils';

const VALIDATED_ATTRIBUTES = [UNSUBSCRIBE_ACTION_KEY, SOLICIT_CODE_KEY].map(
  (key) => `unsubscribe_sync_preference.${key}`,
);

interface IRaisersEdgeSave {
  /** Whether a staged attribute has unsaved changes. Other dirty attributes don't count. */
  isDirty: boolean;
  isSaving: boolean;
  /** Persists the changed staged attributes in one PATCH. */
  handleSave: () => Promise<void>;
  /** The first validation error of the staged changes, which disables Save & Sync. */
  saveDisabledReason?: string;
}

/** Page-level Save & Sync for the changes the tabs stage on the integration. */
export function useRaisersEdgeSave(
  integration: BlackbaudRaisersEdgeIntegration | undefined,
): IRaisersEdgeSave {
  const { t } = useTranslation();
  const [isSaving, setIsSaving] = useState(false);
  // State lags a render behind; the ref blocks a second click that lands before the button disables.
  const isSavingRef = useRef(false);
  // A disconnected integration hides its tabs, so its staged edits can't be saved.
  const isActive = integration?.isConnected || integration?.isPaused;
  const changedAttributes = integration && isActive ? getChangedAttributes(integration) : [];
  const isDirty = changedAttributes.length > 0;

  function getSaveDisabledReason(): string | undefined {
    if (!integration || !isDirty) {
      return undefined;
    }
    // `isValid` is memoized; `validate` is not, so only run it to read the message.
    if (!integration.isValid(VALIDATED_ATTRIBUTES, false)) {
      return integration.validate(VALIDATED_ATTRIBUTES, false).errors[0];
    }
    // Only a save that touches donation upsync needs a fund, so a bad saved state can't block others.
    const touchesDonations =
      changedAttributes.includes('upsync_activities') || changedAttributes.includes('fundraising');
    if (
      touchesDonations &&
      isFundMissing({
        fundraising: integration.get('fundraising'),
        upsync_activities: integration.get('upsync_activities'),
      })
    ) {
      return t('Select a fund to sync donations to Blackbaud.');
    }
    return undefined;
  }

  function handleSuccess(): void {
    // Same event as v1, sent when a save turns donation upsync on.
    if (
      integration &&
      changedAttributes.includes('upsync_activities') &&
      integration.get('upsync_activities')?.donations
    ) {
      logUserEvents({ integrated_renxt_fundraising: { account_id: integration.get('account') } });
    }
    toast(t('Your changes have been saved.'), { type: 'success' });
  }

  async function handleSave(): Promise<void> {
    if (!integration || isSavingRef.current) {
      return;
    }

    isSavingRef.current = true;
    setIsSaving(true);
    try {
      await submitModelWithErrorHandling({
        operation: integration.patch(getPatch(integration, changedAttributes)),
        onSuccess: handleSuccess,
        t,
      });
    } finally {
      isSavingRef.current = false;
      setIsSaving(false);
    }
  }

  return { handleSave, isDirty, isSaving, saveDisabledReason: getSaveDisabledReason() };
}
